import { useQuery } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';

import { analyticsRepository, type AnalyticsRange } from '../api/analytics.repository';

export const analyticsKeys = {
  all: ['admin', 'analytics'] as const,
  summary: () => [...analyticsKeys.all, 'summary'] as const,
  requestsTimeseries: (range: AnalyticsRange) =>
    [...analyticsKeys.all, 'requestsTimeseries', range] as const,
  requestsByCity: () => [...analyticsKeys.all, 'requestsByCity'] as const,
  requestsBySlot: () => [...analyticsKeys.all, 'requestsBySlot'] as const,
  signupsTimeseries: (range: AnalyticsRange) =>
    [...analyticsKeys.all, 'signupsTimeseries', range] as const,
  supportTimeseries: (range: AnalyticsRange) =>
    [...analyticsKeys.all, 'supportTimeseries', range] as const,
};

/** Headline KPI row for the top of the Insights dashboard. */
export function useAnalyticsSummary() {
  return useQuery({
    queryKey: analyticsKeys.summary(),
    queryFn: () => unwrap(analyticsRepository.summary()),
  });
}

/** Coverage-request demand over time, bucketed + status split. Granularity and
 * the date range are query-key inputs, so switching the control refetches. */
export function useRequestsTimeseries(range: AnalyticsRange) {
  return useQuery({
    queryKey: analyticsKeys.requestsTimeseries(range),
    queryFn: () => unwrap(analyticsRepository.requestsTimeseries(range)),
  });
}

/** All-time demand ranked by masjid city ("which area is more demanding"). */
export function useRequestsByCity() {
  return useQuery({
    queryKey: analyticsKeys.requestsByCity(),
    queryFn: () => unwrap(analyticsRepository.requestsByCity()),
  });
}

/** All-time demand by prayer slot. */
export function useRequestsBySlot() {
  return useQuery({
    queryKey: analyticsKeys.requestsBySlot(),
    queryFn: () => unwrap(analyticsRepository.requestsBySlot()),
  });
}

/** New signups over time, split by role and freelance/appointed mode. */
export function useSignupsTimeseries(range: AnalyticsRange) {
  return useQuery({
    queryKey: analyticsKeys.signupsTimeseries(range),
    queryFn: () => unwrap(analyticsRepository.signupsTimeseries(range)),
  });
}

/** New support tickets filed over time (open backlog lives in the summary). */
export function useSupportTimeseries(range: AnalyticsRange) {
  return useQuery({
    queryKey: analyticsKeys.supportTimeseries(range),
    queryFn: () => unwrap(analyticsRepository.supportTimeseries(range)),
  });
}
